import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { DxDropDownBoxModule, DxTreeViewModule } from "devextreme-angular";
import notify from "devextreme/ui/notify";
import { AppGlobals } from "src/app/app.globals";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { TemplateManagementService } from "src/app/services/template-management.service";

interface FolderTreeNode {
  id: number;
  parentId: number | null;
  name: string;
}

/** Contracts admin "Templates" tab (#687/#690): links a template folder whose subtree scopes contract templates. */
@Component({
    selector: "app-contract-template-settings",
    imports: [CommonModule, DxDropDownBoxModule, DxTreeViewModule, SvgIconComponent, GpdButtonComponent],
    templateUrl: "./contract-template-settings.component.html",
    styleUrls: ["./contract-template-settings.component.css"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractTemplateSettingsComponent implements OnInit {
  folderTree: FolderTreeNode[] = [];
  folderId: number | null = null;
  selectedFolderLabel: string | null = null;
  folderDropDownOpened = false;

  isLoading = false;
  isSaving = false;

  private baseline: number | null = null;

  get hasChanges(): boolean {
    return !this.isLoading && this.folderId !== this.baseline;
  }

  constructor(
    private templateService: TemplateManagementService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async load(): Promise<void> {
    this.isLoading = true;
    try {
      const [tree, settings] = await Promise.all([
        this.templateService.getTemplateTree(),
        this.templateService.getContractTemplateSettings(),
      ]);
      if (tree.hasError === true) {
        AppGlobals.ShowErrorMessage(tree.feedback);
      } else {
        this.folderTree = (tree.folderList ?? []).map((f: any) => ({
          id: f.id,
          parentId: f.parentId ?? null,
          name: f.name,
        }));
      }
      if (settings.hasError === true) {
        AppGlobals.ShowErrorMessage(settings.feedback);
      } else {
        this.folderId = settings.contractTemplatesFolderId ?? null;
        this.baseline = this.folderId;
      }
      this.syncSelectedFolderLabel();
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /** Restores the display label for an already-saved folder once the tree is loaded. */
  private syncSelectedFolderLabel(): void {
    this.selectedFolderLabel =
      this.folderId != null ? (this.folderTree.find(f => f.id === this.folderId)?.name ?? null) : null;
  }

  selectFolder(node: FolderTreeNode): void {
    this.folderId = node.id;
    this.selectedFolderLabel = node.name;
    this.folderDropDownOpened = false;
    this.cdr.markForCheck();
  }

  clearFolderSelection(): void {
    this.folderId = null;
    this.selectedFolderLabel = null;
    this.cdr.markForCheck();
  }

  /**
   * The field only ever changes via the tree (selectFolder), which keeps this in sync with
   * selectedFolderLabel exactly. acceptCustomValue lets the input also take free-typed text
   * (DevExtreme has no flat dataSource to validate against here) — if the committed value
   * doesn't match the last real selection, treat it as free text and clear rather than leave a
   * mismatched display next to a stale id that would still save.
   */
  onFolderFieldValueChanged(e: any): void {
    if (e.value == null || e.value !== this.selectedFolderLabel) this.clearFolderSelection();
  }

  cancel(): void {
    this.folderId = this.baseline;
    this.syncSelectedFolderLabel();
    this.cdr.markForCheck();
  }

  async save(): Promise<void> {
    this.isSaving = true;
    try {
      const result = await this.templateService.saveContractTemplateSettings({
        ContractTemplatesFolderId: this.folderId,
      });
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        notify(
          $localize`:@@contractTemplateSettingsSaved:Template settings saved.`,
          "success",
          3000
        );
        this.baseline = this.folderId;
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }
}
